import { PropsWithChildren, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "../context/AuthProvider";
import { ToastProvider } from "../components/common/ToastProvider";
import { ConfirmProvider } from "../components/common/ConfirmProvider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Native <input type="date"> only opens its calendar picker when you click the small
 * calendar icon at the edge of the field, not when you click anywhere else in the box —
 * a common source of "the date field doesn't do anything" confusion. This delegates a
 * single click listener for the whole app (rather than touching every date field
 * individually) and forces the picker open via the standard showPicker() API whenever any
 * date input is clicked anywhere.
 */
function useOpenDatePickerOnClick() {
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      const input = target?.closest('input[type="date"]') as HTMLInputElement | null;
      if (!input || input.disabled || input.readOnly) return;
      if (typeof input.showPicker !== "function") return;
      try {
        input.showPicker();
      } catch {
        // Not supported in this browser, or not triggered by a user gesture — the input
        // still falls back to its normal click-to-open behaviour, so nothing else to do.
      }
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);
}

export function AppProviders({ children }: PropsWithChildren) {
  useOpenDatePickerOnClick();

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>{children}</AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
